/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    Heading,
    IComponentNodeProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { HOME_PAGE, MAORI_LANG } from '../../utilities/constants';
import { getToggleLang, getUserPreferredLanguage, getUserPrefLang } from '../../utilities/language-change';

import { IMoeTileListDescriptionOnlyProps, ITitleData } from './moe-tile-list-description-only.props.autogenerated';

/**
 * Tile-list view props.
 */
export interface ITileListViewProps extends IMoeTileListDescriptionOnlyProps<{}> {
    title?: React.ReactNode;
    tiles: React.ReactNode[];
    tileItemContainer: INodeProps;
    tileListContainer: INodeProps;
    tileListHeading: INodeProps;
    singleSlideCarouselComponentProps: INodeProps;
}

/**
 *
 * TileList component.
 * @extends {React.PureComponent<IMoeTileListDescriptionOnlyProps<{}>>}
 */
export class TileList extends React.PureComponent<IMoeTileListDescriptionOnlyProps<{}>> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: IMoeTileListDescriptionOnlyProps<{}>) {
        super(props);
    }

    private static _createHeading(heading?: ITitleData): React.ReactNode | null {
        if (!heading || !heading.text || StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }
        return <Heading headingTag={heading.tag} text={heading.text} className='ms-tile-list__title' />;
    }

    public render(): JSX.Element | null {
        const { resources, context } = this.props;
        const { title, className, siteName, titleInMaori } = this.props.config;
        const propsCarousel: IComponentNodeProps<ISingleSlideCarouselProps> = {
            tag: SingleSlideCarousel,
            className: this.props.config.className ?? '',
            flipperPrevLabel: resources.flipperPrevious,
            flipperNextLabel: resources.flipperNext,
            parentId: this.props.id,
            telemetryContent: this._telemetryContent,
            vertical: false
        };

        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        const preferredLanguage: any = getUserPreferredLanguage(siteName, HOME_PAGE, userPrefLang, toggleLang, context);

        let titleHeading: any = preferredLanguage === MAORI_LANG ? titleInMaori : title;
        if (!titleInMaori) {
            titleHeading = title;
        }

        const viewProps = {
            ...this.props,
            title: TileList._createHeading(titleHeading),
            singleSlideCarouselComponentProps: propsCarousel,
            tileItemContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-tile__item',
                role: 'listitem'
            },
            tileListContainer: { className: classnames('ms-tile-list', className) },
            tileListHeading: { className: 'ms-tile-list__heading' },
            tiles: this.props.slots.content
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
}

export default TileList;
